'use client';

import { CheckIcon, ChevronRightIcon, CircleIcon } from 'lucide-react';
import {
  Sub,
  Root,
  Item,
  Group,
  Label,
  Portal,
  Trigger,
  Content,
  RadioItem,
  Separator,
  RadioGroup,
  SubContent,
  SubTrigger,
  CheckboxItem,
  ItemIndicator,
} from '@radix-ui/react-dropdown-menu';

import type { ComponentProps, JSX } from 'react';

import { cn } from '~/utils';

export function DropdownMenu(props: ComponentProps<typeof Root>): JSX.Element {
  return <Root data-slot='dropdown-menu' {...props} />;
}

export function DropdownMenuPortal(
  props: ComponentProps<typeof Portal>,
): JSX.Element {
  return <Portal data-slot='dropdown-menu-portal' {...props} />;
}

export function DropdownMenuTrigger(
  props: ComponentProps<typeof Trigger>,
): JSX.Element {
  return <Trigger data-slot='dropdown-menu-trigger' {...props} />;
}

export function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}: ComponentProps<typeof Content>): JSX.Element {
  return (
    <Portal>
      <Content
        sideOffset={sideOffset}
        data-slot='dropdown-menu-content'
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md',
          className,
        )}
        {...props}
      />
    </Portal>
  );
}

export function DropdownMenuGroup(
  props: ComponentProps<typeof Group>,
): JSX.Element {
  return <Group data-slot='dropdown-menu-group' {...props} />;
}

interface IDropdownMenuItemProps extends ComponentProps<typeof Item> {
  inset?: boolean;
  variant?: 'default' | 'destructive';
}

export function DropdownMenuItem({
  inset,
  className,
  variant = 'default',
  ...props
}: IDropdownMenuItemProps): JSX.Element {
  return (
    <Item
      data-inset={inset}
      data-variant={variant}
      data-slot='dropdown-menu-item'
      className={cn(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        'cursor-pointer',
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuCheckboxItem({
  checked,
  children,
  className,
  ...props
}: ComponentProps<typeof CheckboxItem>): JSX.Element {
  return (
    <CheckboxItem
      checked={checked}
      data-slot='dropdown-menu-checkbox-item'
      className={cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className='pointer-events-none absolute left-2 flex size-3.5 items-center justify-center'>
        <ItemIndicator>
          <CheckIcon className='size-4' />
        </ItemIndicator>
      </span>
      {children}
    </CheckboxItem>
  );
}

export function DropdownMenuRadioGroup(
  props: ComponentProps<typeof RadioGroup>,
): JSX.Element {
  return <RadioGroup data-slot='dropdown-menu-radio-group' {...props} />;
}

export function DropdownMenuRadioItem({
  children,
  className,
  ...props
}: ComponentProps<typeof RadioItem>): JSX.Element {
  return (
    <RadioItem
      data-slot='dropdown-menu-radio-item'
      className={cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className='pointer-events-none absolute left-2 flex size-3.5 items-center justify-center'>
        <ItemIndicator>
          <CircleIcon className='size-2 fill-current' />
        </ItemIndicator>
      </span>
      {children}
    </RadioItem>
  );
}

interface IDropdownMenuLabelProps extends ComponentProps<typeof Label> {
  inset?: boolean;
}

export function DropdownMenuLabel({
  inset,
  className,
  ...props
}: IDropdownMenuLabelProps): JSX.Element {
  return (
    <Label
      data-inset={inset}
      data-slot='dropdown-menu-label'
      className={cn(
        'px-2 py-1.5 text-sm font-medium data-[inset]:pl-8',
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({
  className,
  ...props
}: ComponentProps<typeof Separator>): JSX.Element {
  return (
    <Separator
      data-slot='dropdown-menu-separator'
      className={cn('bg-border -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

export function DropdownMenuShortcut({
  className,
  ...props
}: ComponentProps<'span'>): JSX.Element {
  return (
    <span
      data-slot='dropdown-menu-shortcut'
      className={cn(
        'text-muted-foreground ml-auto text-xs tracking-widest',
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSub(
  props: ComponentProps<typeof Sub>,
): JSX.Element {
  return <Sub data-slot='dropdown-menu-sub' {...props} />;
}

interface IDropdownMenuSubTriggerProps
  extends ComponentProps<typeof SubTrigger> {
  inset?: boolean;
}

export function DropdownMenuSubTrigger({
  inset,
  children,
  className,
  ...props
}: IDropdownMenuSubTriggerProps): JSX.Element {
  return (
    <SubTrigger
      data-inset={inset}
      data-slot='dropdown-menu-sub-trigger'
      className={cn(
        'focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8',
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className='ml-auto size-4' />
    </SubTrigger>
  );
}

export function DropdownMenuSubContent({
  className,
  ...props
}: ComponentProps<typeof SubContent>): JSX.Element {
  return (
    <SubContent
      data-slot='dropdown-menu-sub-content'
      className={cn(
        'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg',
        className,
      )}
      {...props}
    />
  );
}
